import type { SetDefinition } from './types';
import type { ListFilters } from '@/components/reports/lists/types';
import type { AnalyticsSummaryRequest } from '@/utils/analyticsSummaryAPI';

/**
 * Convert SetDefinition to ListFilters format for API calls
 */
export function setDefinitionToFilters(setDefinition: SetDefinition): ListFilters {
  const filters: ListFilters = {};

  // API now supports category_id as an array
  if (setDefinition.categories && setDefinition.categories.length > 0) {
    filters.category_id = setDefinition.categories;
  }

  if (setDefinition.employees && setDefinition.employees.length > 0) {
    filters.employees = setDefinition.employees;
  }

  return filters;
}

/**
 * Convert SetDefinition to AnalyticsSummaryRequest format
 * - "Current" period becomes { period: "latest" }
 * - Array of quarters becomes { periods: [...] }
 */
export function setDefinitionToAnalyticsRequest(setDefinition: SetDefinition): AnalyticsSummaryRequest {
  const filters = setDefinitionToFilters(setDefinition);

  // Only include filters if there are actual filter values
  const hasFilters = Object.keys(filters).length > 0;

  console.log('🔍 [setUtils] Converting setDefinition to AnalyticsRequest:', JSON.stringify({
    period: setDefinition.period,
    categories: setDefinition.categories,
    employees: setDefinition.employees,
    filters: hasFilters ? filters : undefined
  }, null, 2));

  if (Array.isArray(setDefinition.period)) {
    // Historical mode: use periods array
    const request: AnalyticsSummaryRequest = {
      periods: setDefinition.period
    };
    if (hasFilters) {
      request.filters = filters;
    }
    console.log('📤 [setUtils] Historical mode request:', JSON.stringify(request, null, 2));
    return request;
  } else if (setDefinition.period === 'Current') {
    // Current mode: use period: "latest"
    const request: AnalyticsSummaryRequest = {
      period: 'latest'
    };
    if (hasFilters) {
      request.filters = filters;
    }
    console.log('📤 [setUtils] Current mode request:', JSON.stringify(request, null, 2));
    return request;
  } else {
    // Single quarter mode: use period (not periods array)
    const request: AnalyticsSummaryRequest = {
      period: setDefinition.period
    };
    if (hasFilters) {
      request.filters = filters;
    }
    console.log('📤 [setUtils] Single quarter mode request:', JSON.stringify(request, null, 2));
    return request;
  }
}

/**
 * Get the period value for API calls (legacy function for backwards compatibility)
 * "Current" should be converted to "latest"
 * For arrays, return the first quarter
 */
export function getPeriodForAPI(period: string | string[]): string {
  if (Array.isArray(period)) {
    return period[0];
  }
  return period === 'Current' ? 'latest' : period;
}
